import { NgClass } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Observable, Subject } from 'rxjs';
import { AbstractLazyDirective } from '@bn/shared/entities/abstracts/abstract-lazy.directive';
import { ClickActionEvent, SupportDepartmentType } from '@bn/shared/entities/index';
import { PullZoneStore, StoreRequest } from '@bn/shared/state-store';
import { LazyLoadDirective } from '@bn/web-ui';
import { AlertComponent } from '@bn/web-ui/alert/alert.component';
import { ButtonComponent } from '@bn/web-ui/button/button.component';
import { ModalState } from '@bn/web-ui/dialog';
import { ListWithIconItem } from '@bn/web-ui/premium-banner';
import { ListWithIconComponent } from '@bn/web-ui/premium-banner/list-with-icon/list-with-icon.component';
import { PlanType } from '../../enums';
import { ShieldApiStore } from '../../stores';

@Component({
  templateUrl: './shield-upgrade-modal.component.html',
  styleUrls: ['./shield-upgrade-modal.component.scss'],
  imports: [AlertComponent, ButtonComponent, ListWithIconComponent, FaIconComponent, LazyLoadDirective, NgClass],
})
export class ShieldUpgradeModalComponent extends AbstractLazyDirective implements OnInit {
  private readonly dialog = inject<BsModalRef<ShieldUpgradeModalComponent>>(BsModalRef);

  readonly #shieldStore = inject(ShieldApiStore);
  readonly #pullZoneStore = inject(PullZoneStore);

  public data?: ModalState;

  public getSelectedShieldId = this.#shieldStore.selectedEntityId();
  public getSelectedPullZone = this.#pullZoneStore.selectedEntity();
  public isLoading = this.#shieldStore.isLoading(StoreRequest.Update, ['upgrade']);
  public isSuccess = this.#shieldStore.isFinished(StoreRequest.Update, ['upgrade']);
  public isError = this.#shieldStore.isError(StoreRequest.Update, ['upgrade']);
  public showSuccessScreen = signal<boolean>(false);

  protected readonly PlanType = PlanType;
  private readonly onAction = new Subject<ClickActionEvent>();

  public listData?: ListWithIconItem[] = [];
  public requiredPlan?: PlanType;

  constructor() {
    super();
    const dialog = this.dialog;

    this.subscriptions.add(dialog.onHide?.subscribe((event) => (event === 'backdrop-click' || event === 'esc' ? this.onClose() : undefined)));

    toObservable(this.isSuccess)
      .pipe(takeUntilDestroyed())
      .subscribe((isSuccess) => {
        if (isSuccess) {
          const shieldZoneId = Number(this.#shieldStore.selectedEntityId()());

          if (!shieldZoneId || isNaN(shieldZoneId)) return;

          this.showSuccessScreen.set(true);

          this.#shieldStore.clearUpdated();

          this.onAction.next({
            action: 'confirm',
          });
        }
      });
  }

  public ngOnInit(): void {
    this.listData = (this.data?.extra?.itemsList || []) as ListWithIconItem[];
    this.requiredPlan = (this.data?.extra?.requiredPlan as PlanType) ?? undefined;
  }

  public onUpgrade(planType?: PlanType): void {
    const currentShieldId = this.#shieldStore.selectedEntityId()();

    if (!currentShieldId) return;

    if (planType === PlanType.Enterprise) {
      this.onContactSupportClick();

      return;
    }

    this.#shieldStore.updateShield({
      id: +currentShieldId,
      data: { planType },
      customFields: ['upgrade'],
      toastr: {
        title: `Bunny Shield ${PlanType[planType || PlanType.Advanced]}`,
        message: `Bunny Shield ${PlanType[planType || PlanType.Advanced]} was successfully enabled`,
      },
    });
  }

  public getOnAction$(): Observable<ClickActionEvent> {
    return this.onAction.asObservable();
  }

  public onClose(): void {
    this.onAction.next({
      action: 'cancel',
      data: {
        entity: {
          isSuccessScreen: this.showSuccessScreen(),
        },
      },
    });
  }

  private onContactSupportClick(): void {
    const currentShieldId = this.getSelectedShieldId();
    const Message = `Pull Zone ID: ${this.getSelectedPullZone()?.Id} \nBunny Shield ID: ${currentShieldId}`;

    this.router.navigate([`support/tickets/new`], {
      queryParams: {
        Department: SupportDepartmentType.Sales,
        Issue: 7,
        Subject: `I’m interested in Bunny Shield Enterprise`,
        Message,
      },
    });

    this.dialog.hide();
  }
}
